// src/components/ProfitTable.tsx (2026-07-01 新設)
// 粗利分析の1軸テーブル(科別/医師別/DPC別 共通)。期間→月平均、比は加重(server側)。
//   8列: 名称 | 収入 | {入院/在院}患者延数 | 1日{入院/在院}単価 | 粗利 | 1日粗利単価 | 群分類 | 粗利率
//   初期ソート=粗利降順。群分類=1日粗利単価を閾値(gun1/2/3)で4分類。CSV出力・行クリックで絞り込み/drill。
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters, effectiveDpcSelection } from "../store";
import { useProfitPeriod } from "../lib/useProfitPeriod";
import { CsvButton } from "./CsvButton";
import { useSort, SortTh } from "../lib/useSort";
import { procHover } from "../lib/dpcHover";
import type { ProfitResponse, ProfitRow } from "@shared/api";
import { IS_THETA, gpText } from "../lib/gpMode";

// [中央値 2026-07-22 ユーザ指示] DPC別の 1日単価だけ「患者1人ずつの1日単価」の中央値にする。
//   科別/医師別は従来どおり加重平均 (Σ収入・Σ粗利 ÷ Σ延数)。DPC別は 1 疾患の中に長期入院例が
//   混ざると分母を食って単価が下へ引っ張られるため、代表値として中央値を使う。
const UNIT_MEDIAN_NOTE =
  "※中央値: 患者(急性期エピソード)1人ずつの1日単価 (その患者の収入または粗利 ÷ その患者の入院日数) の中央値。"
  + "\n長期入院の外れ値に引っ張られないようにするため、合計÷合計 (加重平均) ではありません。"
  + "\nしたがって 1日単価 × 延数 は 収入・粗利 の合計には一致しません。"
  + "\n(中央値にしているのは DPC別のみ。科別/医師別は 合計÷合計 のまま)";

const sen = (v: number | null | undefined) => v == null ? "—" : Math.round(v / 1000).toLocaleString();  // 円→千円
const yen = (v: number | null | undefined) => v == null ? "—" : Math.round(v).toLocaleString();          // 円
const num = (v: number | null | undefined) => v == null ? "—" : Math.round(v).toLocaleString();
const pct = (v: number | null | undefined) => v == null ? "—" : (v * 100).toFixed(1) + "%";
// [GP率 2026-08-24 ユーザ指示] GP率 = GP ÷ 医師人件費。率(%)でなく**倍率**なので小数第2位まで。
//   負にもなりうる (人件費を引いて GP が赤の科) ので符号はそのまま出す。
const mult = (v: number | null | undefined) => v == null ? "—" : v.toFixed(2);
// [科別表 2026-08-26] 横長の行を追いやすくするため、主要金額は桁を圧縮して単位を値に付ける。
//   1億円以上は小数第2位までの億、それ未満は整数の万。負のGPは絶対値でなく符号を保つ。
const compactMoney = (v: number | null | undefined) =>
  v == null ? "—" : Math.abs(v) >= 100_000_000
    ? `${(v / 100_000_000).toFixed(2)}億`
    : `${Math.round(v / 10_000).toLocaleString()}万`;

export function ProfitTable({ axis, dpcLevel, focusedKey, onRowClick, nameHeader, csvName }: {
  axis: "dept" | "doctor" | "dpc";
  dpcLevel?: number;
  focusedKey?: string | null;
  onRowClick?: (row: ProfitRow) => void;
  nameHeader: string;
  csvName: string;
}) {
  const f = useFilters();
  const vol = f.kpiVolumeBase === "在院" ? "在院" : "入院";
  // [2026-07-17] 期間は粗利専用 state (default=最新確定月のみ)
  const period = useProfitPeriod();

  // [階層列 2026-07-17] DPC別 (dpc 軸) の手術別以降は DPC分析主表と同じ
  // 手術分類/処置1分岐/処置2分岐 列を出す (行の識別・深掘り用)。
  const lvl = dpcLevel ?? 6;
  const showOpe = axis === "dpc" && lvl >= 10;
  const showP1  = axis === "dpc" && lvl >= 11;
  const showP2  = axis === "dpc" && lvl >= 12;
  // [2026-07-18] 退院日基準はエピソード単位 → 退院数・入院日数中央値を表示 (在院日基準は概念が無い)
  const isDischarge = f.profitBasis === "退院";
  // [中央値 2026-07-22] 1日単価が中央値で返ってくる軸 (= DPC別・退院日基準)。server の unitStat と同判定。
  const isMedian = axis === "dpc" && isDischarge;
  const nCols = (axis === "dept" ? 9 : 10) + (showOpe ? 1 : 0) + (showP1 ? 1 : 0) + (showP2 ? 1 : 0) + (isDischarge ? 2 : 0);

  // [絞り込み保持 2026-07-17] DPC別の絞り込みは DPC分析タブと共通の
  // dpc6/selectedDpc10-14 から「表示階層で有効な最深コード」を 1 本に縮約して
  // 前方一致 (server: dpcParent) で絞る。階層は前方一致で包含関係にあるため 1 本で足りる。
  // 下位階層の絞り込みは上位階層の表示には反映しない (effectiveDpcSelection がゲート)。
  const dpcSel = axis === "dpc" ? (effectiveDpcSelection(f) ?? f.dpc6 ?? null) : null;

  const { data, isLoading } = useQuery<ProfitResponse>({
    queryKey: ["profit", axis, f.profitBasis, dpcLevel, dpcSel, f.granularity, f.yotei, f.profitIryo, f.kpiVolumeBase, period.from, period.to, f.focusedDept, f.focusedDoctor, f.routeKeiyu],
    queryFn: () => api.profit({
      axis, basis: f.profitBasis, granularity: f.granularity, dpcLevel, yotei: f.yotei, iryo: f.profitIryo, volBase: f.kpiVolumeBase,
      dateFrom: period.from, dateTo: period.to,
      focusedDept:   axis === "dept" ? null : (f.focusedDept ?? null),     // 科別自身は全科、医師/DPCは科で絞る
      focusedDoctor: axis === "dpc"  ? (f.focusedDoctor ?? null) : null,   // DPC別のみ医師で絞る
      dpcParent:     dpcSel,
      keiyu:         f.routeKeiyu,
    }),
    placeholderData: (p) => p,
  });

  // [病院全体 2026-07-17] key='__total__' 行はソート対象から外し、常に先頭に固定表示。
  const totalRow = useMemo(() => (data?.rows ?? []).find((r) => r.key === "__total__") ?? null, [data]);
  const rows = useMemo(
    // ★2026-08-22: 粗利 は null をとる (GP 未算出期間)。null は末尾へ。
    () => (data?.rows ?? []).filter((r) => r.key !== "__total__").slice()
      .sort((a, b) => (b.粗利 ?? -Infinity) - (a.粗利 ?? -Infinity)),
    [data],
  );
  const s = useSort(rows);  // 初期=粗利降順(rows原順)。ヘッダクリックで並べ替え。
  const gunOf = (gpT: number | null): string =>
    gpT == null ? "—" : gpT >= f.gun1 ? "1群" : gpT >= f.gun2 ? "2群" : gpT >= f.gun3 ? "3群" : "4群";
  const gunColor: Record<string, string> = { "1群": "bg-emerald-100 text-emerald-800", "2群": "bg-sky-100 text-sky-800", "3群": "bg-amber-100 text-amber-800", "4群": "bg-slate-100 text-slate-600" };

  return (
    <div>
      <div className="profit-table-meta flex items-center gap-2 mb-1">
        <span className="text-xs text-slate-500">{rows.length} 件{isLoading && " · loading…"}{data && (data.nMonths > 1 ? ` · 期間 ${data.nMonths}ヶ月の月平均` : "")}</span>
        {/* [GP の被覆 2026-08-22] 期間内に粗利を算出できない月があると server が粗利系を
            null で返す。「なぜ — なのか」が分からないと 0 と誤読されるので理由を出す。 */}
        {data && data.gpComplete === false && (
          <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
                title={gpText(`選択期間 ${data.nMonths}ヶ月のうち 粗利を算出できるのは ${data.gpMonths ?? 0}ヶ月だけです。`
                  + "\n一部の月だけの合計を期間の全月数で割ると過小な偽の平均になるため、粗利・1日粗利単価・粗利率は表示しません。"
                  + "\n期間を粗利のある月だけに絞ってください。")}>
            {gpText(`⚠ 粗利は非表示 (期間 ${data.nMonths}ヶ月中 ${data.gpMonths ?? 0}ヶ月しか算出できません)`)}
          </span>
        )}
        {/* [医師人件費 2026-08-24] 分母の被覆が足りないときも同じ理由で列を出さない。
            ★正式版は 粗利=薬剤材料費差引後 なので gpComplete=true でもこちらだけ false になりうる
              (医師人件費は 2025-04 以降しか無い)。だから GP の警告とは別に出す。 */}
        {data && data.gpComplete !== false && data.jinComplete === false && (
          <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
                title={`選択期間 ${data.nMonths}ヶ月のうち 医師人件費があるのは ${data.jinMonths ?? 0}ヶ月だけです。`
                  + (IS_THETA
                      ? "\n分子だけ全月・分母は一部の月、という偽の比になるため GP率 も表示しません。"
                      : "")
                  + "\n医師人件費は 2025年4月以降のみです。"}>
            ⚠ 医師人件費{IS_THETA ? "・GP率" : ""}は非表示 (期間 {data.nMonths}ヶ月中 {data.jinMonths ?? 0}ヶ月しか医師人件費がありません)
          </span>
        )}
        {/* [中央値 2026-07-22 ユーザ指示] DPC別だけ 1日単価が中央値であることを画面上でも明示 */}
        {isMedian && (
          <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 cursor-help"
                title={UNIT_MEDIAN_NOTE}>
            ※ 1日単価 = 患者1人ずつの単価の中央値
          </span>
        )}
        <CsvButton
          rows={[...(totalRow ? [totalRow] : []), ...rows].map((r) => ({
            [nameHeader]: r.name,
            ...(showOpe ? { 手術分類: r.手術名 ?? "" } : {}),
            ...(showP1  ? { 処置1分岐: r.処置1名 ?? "" } : {}),
            ...(showP2  ? { 処置2分岐: r.処置2名 ?? "" } : {}),
            ...(isDischarge ? {
              "退院数_件月": Math.round((r.件数 ?? 0) / (data?.nMonths ?? 1)),
              "入院日数中央値_日": r.alos != null ? Math.round(r.alos) : "",
            } : {}),
            ...(axis === "dept" ? {
              "入院収入_千円月": Math.round(r.収入 / 1000),
              "薬剤材料費_千円月": r.薬剤材料費 == null ? "" : Math.round(r.薬剤材料費 / 1000),
              "医師人件費_千円月": r.医師人件費 == null ? "" : Math.round(r.医師人件費 / 1000),
              [gpText("粗利_千円月")]: r.粗利 == null ? "" : Math.round(r.粗利 / 1000),
              "GP率_倍": r.医師人件費比 == null ? "" : Number(r.医師人件費比.toFixed(2)),
              [`${vol}患者延数_人日月`]: Math.round(r.延数),
              [gpText("1日粗利単価_円")]: r["1日粗利単価"] == null ? "" : Math.round(r["1日粗利単価"]),
              "群分類": gunOf(r["1日粗利単価"]),
            } : {
              "収入_千円月": Math.round(r.収入 / 1000), [`${vol}患者延数_人日月`]: Math.round(r.延数),
              // [中央値 2026-07-22] DPC別は中央値なので CSV の列名にも明示する
              [`1日${vol}単価_円${isMedian ? "_中央値" : ""}`]: r["1日入院単価"] == null ? "" : Math.round(r["1日入院単価"]),
              "薬剤材料費_千円月": r.薬剤材料費 == null ? "" : Math.round(r.薬剤材料費 / 1000),
              "医師人件費_千円月": r.医師人件費 == null ? "" : Math.round(r.医師人件費 / 1000),
              [gpText("粗利_千円月")]: r.粗利 == null ? "" : Math.round(r.粗利 / 1000),
              ...(IS_THETA
                ? { "GP率_倍": r.医師人件費比 == null ? "" : Number(r.医師人件費比.toFixed(2)) }
                : { "粗利率_%": r.粗利率 == null ? "" : Number((r.粗利率 * 100).toFixed(1)) }),
              [gpText(`1日粗利単価_円${isMedian ? "_中央値" : ""}`)]: r["1日粗利単価"] == null ? "" : Math.round(r["1日粗利単価"]),
              "群分類": gunOf(r["1日粗利単価"]),
            }),
          }))}
          filename={csvName}
          style={{ marginLeft: "auto" }}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="profit-table text-xs border-collapse w-full">
          <thead>
            <tr className="bg-slate-100 sticky top-0 z-[1]">
              <SortTh s={s} sortKey="名称" get={(r) => (r as ProfitRow).name} className="text-left px-2 py-1 border-b border-slate-200">{nameHeader}</SortTh>
              {showOpe && <SortTh s={s} sortKey="手術分類" get={(r) => (r as ProfitRow).手術名} className="text-left px-2 py-1 border-b border-slate-200">手術分類</SortTh>}
              {showP1  && <SortTh s={s} sortKey="処置1分岐" get={(r) => (r as ProfitRow).処置1名} className="text-left px-2 py-1 border-b border-slate-200">処置1分岐</SortTh>}
              {showP2  && <SortTh s={s} sortKey="処置2分岐" get={(r) => (r as ProfitRow).処置2名} className="text-left px-2 py-1 border-b border-slate-200">処置2分岐</SortTh>}
              {isDischarge && <SortTh s={s} sortKey="退院数" get={(r) => (r as ProfitRow).件数} className="text-right px-2 py-1 border-b border-slate-200">退院数<br/><span className="text-[9px] text-slate-400">件/月</span></SortTh>}
              {isDischarge && <SortTh s={s} sortKey="入院日数中央値" get={(r) => (r as ProfitRow).alos} className="text-right px-2 py-1 border-b border-slate-200">入院日数<br/><span className="text-[9px] text-slate-400">中央値</span></SortTh>}
              {axis === "dept" ? <>
                <SortTh s={s} sortKey="収入" get={(r) => (r as ProfitRow).収入} className="text-right px-2 py-1 border-b border-slate-200">入院収入<br/><span className="text-[9px] text-slate-400">月平均</span></SortTh>
                <SortTh s={s} sortKey="薬剤材料費" get={(r) => (r as ProfitRow).薬剤材料費} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">薬剤材料費<br/><span className="text-[9px] text-slate-400">月平均</span></SortTh>
                <SortTh s={s} sortKey="医師人件費" get={(r) => (r as ProfitRow).医師人件費} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">医師人件費<br/><span className="text-[9px] text-slate-400">月平均</span></SortTh>
                <SortTh s={s} sortKey="粗利" get={(r) => (r as ProfitRow).粗利} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">{gpText("粗利")}<br/><span className="text-[9px] text-slate-400">月平均</span></SortTh>
                <SortTh s={s} sortKey="粗利率" get={(r) => (r as ProfitRow).医師人件費比} className="text-right px-2 py-1 border-b border-slate-200"
                        title={"GP率 = GP ÷ 医師人件費 (倍)。\n(クリックで昇順/降順)"}>GP率<br/><span className="text-[9px] text-slate-400">倍</span></SortTh>
                <SortTh s={s} sortKey="延数" get={(r) => (r as ProfitRow).延数} className="text-right px-2 py-1 border-b border-slate-200">{vol}患者延数<br/><span className="text-[9px] text-slate-400">人日/月</span></SortTh>
                <SortTh s={s} sortKey="1日粗利単価" get={(r) => (r as ProfitRow)["1日粗利単価"]} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">{gpText("1日粗利単価")}<br/><span className="text-[9px] text-slate-400">円/日</span></SortTh>
                <SortTh s={s} sortKey="群分類" get={(r) => (r as ProfitRow)["1日粗利単価"]} className="text-center px-2 py-1 border-b border-slate-200">群分類</SortTh>
              </> : <>
                <SortTh s={s} sortKey="収入" get={(r) => (r as ProfitRow).収入} className="text-right px-2 py-1 border-b border-slate-200">収入<br/><span className="text-[9px] text-slate-400">千円/月</span></SortTh>
                <SortTh s={s} sortKey="延数" get={(r) => (r as ProfitRow).延数} className="text-right px-2 py-1 border-b border-slate-200">{vol}患者延数<br/><span className="text-[9px] text-slate-400">人日/月</span></SortTh>
                <SortTh s={s} sortKey="1日単価" get={(r) => (r as ProfitRow)["1日入院単価"]} className="text-right px-2 py-1 border-b border-slate-200"
                        title={isMedian ? gpText(`${UNIT_MEDIAN_NOTE}\n(クリックで昇順/降順)`) : undefined}>1日{vol}単価{isMedian && " ※"}<br/><span className="text-[9px] text-slate-400">円/日{isMedian && "・中央値"}</span></SortTh>
                <SortTh s={s} sortKey="薬剤材料費" get={(r) => (r as ProfitRow).薬剤材料費} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">薬剤材料費<br/><span className="text-[9px] text-slate-400">千円/月</span></SortTh>
                <SortTh s={s} sortKey="医師人件費" get={(r) => (r as ProfitRow).医師人件費} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">医師人件費<br/><span className="text-[9px] text-slate-400">千円/月</span></SortTh>
                <SortTh s={s} sortKey="粗利" get={(r) => (r as ProfitRow).粗利} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">{gpText("粗利")}<br/><span className="text-[9px] text-slate-400">千円/月</span></SortTh>
                <SortTh s={s} sortKey="粗利率" get={(r) => IS_THETA ? (r as ProfitRow).医師人件費比 : (r as ProfitRow).粗利率} className="text-right px-2 py-1 border-b border-slate-200">{gpText("粗利率")}</SortTh>
                <SortTh s={s} sortKey="1日粗利単価" get={(r) => (r as ProfitRow)["1日粗利単価"]} className="text-right px-2 py-1 border-b border-slate-200 bg-emerald-50">{gpText("1日粗利単価")}{isMedian && " ※"}<br/><span className="text-[9px] text-slate-400">円/日{isMedian && "・中央値"}</span></SortTh>
                <SortTh s={s} sortKey="群分類" get={(r) => (r as ProfitRow)["1日粗利単価"]} className="text-center px-2 py-1 border-b border-slate-200">群分類</SortTh>
              </>}
            </tr>
          </thead>
          <tbody>
            {/* [病院全体 2026-07-17] 科別の先頭に固定 (ソート非対象・クリック不可) */}
            {totalRow && (() => {
              const g = gunOf(totalRow["1日粗利単価"]);
              return (
                <tr key="__total__" className="bg-slate-100/90 font-semibold border-b-2 border-slate-300">
                  <td className="text-left px-2 py-1">{totalRow.name}</td>
                  {showOpe && <td className="px-2 py-1" />}
                  {showP1  && <td className="px-2 py-1" />}
                  {showP2  && <td className="px-2 py-1" />}
                  {isDischarge && <td className="text-right px-2 py-1 tabular-nums">{num(totalRow.件数 / (data?.nMonths ?? 1))}</td>}
                  {isDischarge && <td className="text-right px-2 py-1 tabular-nums">{totalRow.alos != null ? `${Math.round(totalRow.alos)} 日` : "—"}</td>}
                  {axis === "dept" ? <>
                    <td className="text-right px-2 py-1 tabular-nums">{compactMoney(totalRow.収入)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{compactMoney(totalRow.薬剤材料費)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{compactMoney(totalRow.医師人件費)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{compactMoney(totalRow.粗利)}</td>
                    <td className="text-right px-2 py-1 tabular-nums">{mult(totalRow.医師人件費比)}</td>
                    <td className="text-right px-2 py-1 tabular-nums">{num(totalRow.延数)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{yen(totalRow["1日粗利単価"])}</td>
                    <td className="text-center px-2 py-1"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${gunColor[g] ?? ""}`}>{g}</span></td>
                  </> : <>
                    <td className="text-right px-2 py-1 tabular-nums">{sen(totalRow.収入)}</td>
                    <td className="text-right px-2 py-1 tabular-nums">{num(totalRow.延数)}</td>
                    <td className="text-right px-2 py-1 tabular-nums">{yen(totalRow["1日入院単価"])}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{sen(totalRow.薬剤材料費)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{sen(totalRow.医師人件費)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{sen(totalRow.粗利)}</td>
                    <td className="text-right px-2 py-1 tabular-nums">{IS_THETA ? mult(totalRow.医師人件費比) : pct(totalRow.粗利率)}</td>
                    <td className="text-right px-2 py-1 tabular-nums bg-emerald-50/40">{yen(totalRow["1日粗利単価"])}</td>
                    <td className="text-center px-2 py-1"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${gunColor[g] ?? ""}`}>{g}</span></td>
                  </>}
                </tr>
              );
            })()}
            {s.rows.map((r, rowIndex) => {
              const g = gunOf(r["1日粗利単価"]);
              const focused = focusedKey != null && r.key === focusedKey;
              return (
                <tr
                  key={r.key}
                  className={`${focused
                    ? "bg-amber-100 outline outline-2 outline-red-400 font-semibold"
                    : axis === "dept"
                      ? `${rowIndex % 2 === 0 ? "bg-white" : "bg-slate-100/70"} hover:bg-sky-50`
                      : "hover:bg-slate-50"} ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={() => onRowClick?.(r)}
                  title={onRowClick ? "クリックで絞り込み/深掘り" : undefined}
                >
                  {/* [ホバー 2026-07-18] dpc 軸は DPC分析主表と同じ実績/候補ホバー */}
                  <td className={`text-left px-2 py-1 border-b border-slate-100 ${axis === "dpc" ? "underline decoration-dotted decoration-slate-300 cursor-help" : ""}`}
                      title={axis === "dpc" ? [
                        `DPCコード: ${r.key}`,
                        `疾患: ${r.name}`,
                        r.icd_list ? `── 実績ICD病名 ──\n${r.icd_list.split(" / ").join("\n")}` : "",
                      ].filter(Boolean).join("\n") : undefined}>
                    {focused && "🎯 "}{r.name}
                  </td>
                  {showOpe && (
                    <td className="text-left px-2 py-1 border-b border-slate-100 text-slate-600 cursor-help"
                        title={[
                          `手術分類: ${r.手術名}`,
                          r.ope_list ? `── 実績手術K ──\n${r.ope_list.split(" / ").join("\n")}` : "",
                        ].filter(Boolean).join("\n")}>
                      {r.手術名}
                    </td>
                  )}
                  {showP1 && (
                    <td className="text-left px-2 py-1 border-b border-slate-100 text-slate-600 cursor-help"
                        title={procHover({ n: 1, branchName: r.処置1名 ?? "", jisseki: r.処置1実績 ?? "",
                          heishi: r.処置1実績_併施 ?? "", candK: r.処置1候補K ?? "", candName: r.処置1候補名 ?? "" })}>
                      {r.処置1名}
                    </td>
                  )}
                  {showP2 && (
                    <td className="text-left px-2 py-1 border-b border-slate-100 text-slate-600 cursor-help"
                        title={procHover({ n: 2, branchName: r.処置2名 ?? "", jisseki: r.処置2実績 ?? "",
                          heishi: r.処置2実績_併施 ?? "", candK: r.処置2候補K ?? "", candName: r.処置2候補名 ?? "" })}>
                      {r.処置2名}
                    </td>
                  )}
                  {isDischarge && <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums">{num(r.件数 / (data?.nMonths ?? 1))}</td>}
                  {isDischarge && <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums">{r.alos != null ? `${Math.round(r.alos)} 日` : "—"}</td>}
                  {axis === "dept" ? <>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums">{compactMoney(r.収入)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums bg-emerald-50/40">{compactMoney(r.薬剤材料費)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums bg-emerald-50/40">{compactMoney(r.医師人件費)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums bg-emerald-50/40 font-semibold">{compactMoney(r.粗利)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums">{mult(r.医師人件費比)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums">{num(r.延数)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-200 tabular-nums bg-emerald-50/40">{yen(r["1日粗利単価"])}</td>
                    <td className="text-center px-2 py-1 border-b border-slate-200"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${gunColor[g] ?? ""}`}>{g}</span></td>
                  </> : <>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums">{sen(r.収入)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums">{num(r.延数)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums">{yen(r["1日入院単価"])}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums bg-emerald-50/40">{sen(r.薬剤材料費)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums bg-emerald-50/40">{sen(r.医師人件費)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums bg-emerald-50/40 font-semibold">{sen(r.粗利)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums">{IS_THETA ? mult(r.医師人件費比) : pct(r.粗利率)}</td>
                    <td className="text-right px-2 py-1 border-b border-slate-100 tabular-nums bg-emerald-50/40">{yen(r["1日粗利単価"])}</td>
                    <td className="text-center px-2 py-1 border-b border-slate-100"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${gunColor[g] ?? ""}`}>{g}</span></td>
                  </>}
                </tr>
              );
            })}
            {rows.length === 0 && !isLoading && (
              <tr><td colSpan={nCols} className="text-center text-slate-400 py-3">該当データなし</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
